import $ from 'jquery';
import { lockPageScroll } from '../../../../assets/js/utils/scroll-lock';
import { initializeCartRecommendations } from '../../../../assets/js/utils/cart-recommendations.mjs';

const initializeCart = () => {
    const dialog = document.querySelector('#sidebar-cart');
    if (typeof HTMLDialogElement === 'undefined' || !(dialog instanceof HTMLDialogElement) || !dialog.showModal) return;
    const trigger = document.querySelector('#open-cart-btn');
    const closeButton = dialog.querySelector('[data-cart-close]');
    const initialFocus = dialog.querySelector('[data-cart-initial-focus]') || closeButton;
    const error = dialog.querySelector('[data-cart-error]');
    const status = dialog.querySelector('[data-cart-status]');
    let busy = false;
    let unlock;
    let closing;
    let opener;
    let refreshQueued = false;
    const couponDetails = new Map();
    const restoredDetails = new WeakSet();
    let couponFocus = null;

    const snapshot = () => dialog.querySelector('.SidebarCart__snapshot');
    const sync = () => {
        const content = snapshot();
        if (!content) return;
        dialog.querySelector('[data-cart-count]').textContent = content.dataset.count;
        content.inert = busy;
        content.setAttribute('aria-busy', String(busy));
        content.querySelectorAll('[data-auto-coupon-details]').forEach((details) => {
            if (restoredDetails.has(details)) return;
            restoredDetails.add(details);
            details.open = couponDetails.get(details.dataset.autoCouponDetails) || false;
        });
        if (!busy && dialog.open && couponFocus && (document.activeElement === dialog || !dialog.contains(document.activeElement))) {
            const target = dialog.querySelector(`[data-cart-focus="${CSS.escape(couponFocus)}"]`);
            (target || closeButton).focus({ preventScroll: true });
        }
    };
    const setBusy = (value) => {
        busy = value;
        dialog.classList.toggle('is-loading', value);
        sync();
    };
    const showError = (message) => {
        error.querySelector('[data-cart-error-message]').textContent = message;
        error.hidden = false;
    };
    const applyFragments = (result) => {
        const scrollTop = dialog.querySelector('[data-cart-scroll]')?.scrollTop || 0;
        Object.entries(result.fragments).forEach(([selector, html]) => $(selector).replaceWith(html));
        const scroll = dialog.querySelector('[data-cart-scroll]');
        if (scroll) scroll.scrollTop = scrollTop;
        // Keep WooCommerce's cache and other tabs in sync without another AJAX round trip.
        const params = window.wc_cart_fragments_params;
        if (params) {
            try {
                sessionStorage.setItem(params.fragment_name, JSON.stringify(result.fragments));
                sessionStorage.setItem(params.cart_hash_key, result.cart_hash);
                localStorage.setItem(params.cart_hash_key, result.cart_hash);
                if (result.cart_hash) sessionStorage.setItem('wc_cart_created', String(Date.now()));
            } catch (_) { /* Private browsing can disable storage; the server cart still works. */ }
        }
        $(document.body).trigger('wc_fragments_refreshed');
    };
    const request = async (action, fields = {}, origin = null) => {
        if (busy) return;
        const focusKey = origin?.dataset.cartFocus;
        const input = origin?.closest('[data-cart-item]')?.querySelector('[data-cart-quantity]');
        const previous = input?.dataset.original;
        const recommendation = origin?.closest('[data-cart-recommendation]');
        const feedback = recommendation?.querySelector('[data-cart-recommendation-feedback]');
        const addedMessage = recommendation?.closest('[data-cart-recommendations]')?.dataset.added;
        error.hidden = true;
        status.textContent = '';
        const body = new URLSearchParams({ ...fields, action, nonce: snapshot()?.dataset.cartNonce || '' });
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 20000);
        setBusy(true);
        if (recommendation) { origin.dataset.state = 'loading'; origin.setAttribute('aria-busy', 'true'); }
        try {
            const response = await fetch(jsData.ajaxUrl, { method: 'POST', credentials: 'same-origin', body, signal: controller.signal });
            const result = await response.json();
            if (!response.ok || !result.fragments) {
                const notice = document.createElement('div');
                if (typeof result.error === 'string') notice.innerHTML = result.error;
                throw new Error(result?.data?.message || notice.textContent.trim() || dialog.dataset.error);
            }
            applyFragments(result);
            if (action !== 'verenza_get_cart_items') {
                status.textContent = recommendation && addedMessage ? addedMessage.replace('{name}', recommendation.dataset.productName) : dialog.dataset.updated;
                document.dispatchEvent(new Event('ovalia:cart-updated'));
            }
        } catch (failure) {
            if (input?.isConnected) input.value = previous;
            const message = failure.name === 'AbortError' ? dialog.dataset.error : failure.message || dialog.dataset.error;
            if (feedback?.isConnected) { feedback.textContent = message; feedback.hidden = false; }
            else showError(message);
        } finally {
            clearTimeout(timeout);
            setBusy(false);
            if (recommendation) { delete origin.dataset.state; origin.removeAttribute('aria-busy'); }
            // Replacing a line must not strand keyboard focus on the page behind the dialog.
            if (dialog.open && focusKey && (document.activeElement === dialog || !dialog.contains(document.activeElement))) {
                const target = dialog.querySelector(`[data-cart-focus="${CSS.escape(focusKey)}"]`);
                const quantity = target?.closest('[data-cart-item]')?.querySelector('[data-cart-quantity]');
                const fallback = recommendation
                    ? dialog.querySelector('[data-cart-recommendation-action]') || initialFocus
                    : quantity || dialog.querySelector('[data-cart-remove]') || closeButton;
                (target && !target.disabled ? target : fallback).focus({ preventScroll: true });
            }
            if (refreshQueued) { refreshQueued = false; refresh(); }
        }
    };
    const refresh = () => {
        if (busy) { refreshQueued = true; return; }
        request('verenza_get_cart_items');
    };
    const close = () => {
        if (!dialog.open || closing) return;
        dialog.classList.add('is-closing');
        const delay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220;
        closing = setTimeout(() => dialog.close(), delay);
    };
    const open = () => {
        if (dialog.open) return;
        opener = document.activeElement;
        dialog.classList.remove('is-closing');
        unlock = lockPageScroll();
        dialog.showModal();
        trigger?.setAttribute('aria-expanded', 'true');
        initialFocus.focus({ preventScroll: true });
        sync();
        refresh();
    };
    trigger?.addEventListener('click', (event) => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        open();
    });
    document.addEventListener('ovalia:open-cart', open);
    dialog.addEventListener('toggle', (event) => {
        if (event.target.matches('[data-auto-coupon-details]')) {
            couponDetails.set(event.target.dataset.autoCouponDetails, event.target.open);
        }
    }, true);
    dialog.addEventListener('focusin', (event) => {
        couponFocus = event.target.closest('[data-auto-coupon-details]')?.querySelector('summary')?.dataset.cartFocus || null;
    });
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
    dialog.addEventListener('close', () => {
        clearTimeout(closing); closing = null;
        unlock?.(); unlock = null;
        dialog.classList.remove('is-closing');
        trigger?.setAttribute('aria-expanded', 'false');
        if (opener?.isConnected) opener.focus({ preventScroll: true });
    });
    let outside = false;
    const isOutside = (event) => {
        const rect = dialog.getBoundingClientRect();
        return event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom);
    };
    dialog.addEventListener('pointerdown', (event) => { outside = isOutside(event); });
    dialog.addEventListener('click', (event) => {
        if (outside && isOutside(event)) { close(); return; }
        if (event.target.closest('[data-cart-close]')) { close(); return; }
        if (event.target.closest('[data-cart-retry]')) { refresh(); return; }
        if (busy) return;
        const line = event.target.closest('[data-cart-item]');
        if (!line) return;
        const remove = event.target.closest('[data-cart-remove]');
        if (remove) { request('remove_item_from_cart', { cart_item_key: line.dataset.cartItem }, remove); return; }
        const step = event.target.closest('[data-cart-step]');
        if (step) {
            const input = line.querySelector('[data-cart-quantity]');
            const quantity = Number(input.value) + Number(step.dataset.cartStep);
            if (quantity < 1 || (input.hasAttribute('max') && quantity > Number(input.max))) return;
            input.value = quantity;
            request('verenza_update_cart_quantity', { cart_item_key: line.dataset.cartItem, quantity }, step);
        }
    });
    dialog.addEventListener('change', (event) => {
        const input = event.target.closest('[data-cart-quantity]');
        if (!input || busy) return;
        if (!input.checkValidity()) { input.reportValidity(); input.value = input.dataset.original; return; }
        if (input.value === input.dataset.original) return;
        request('verenza_update_cart_quantity', { cart_item_key: input.closest('[data-cart-item]').dataset.cartItem, quantity: input.value }, input);
    });
    // All existing add-to-cart integrations share WooCommerce's fragment channel.
    $(document.body).on('wc_fragments_refreshed wc_fragments_loaded added_to_cart', sync);
    initializeCartRecommendations(dialog, {
        ajaxUrl: jsData.ajaxUrl,
        isBusy: () => busy,
        purchase: (fields, origin) => request('verenza_add_to_cart', fields, origin),
    });
    new MutationObserver(sync).observe(dialog.querySelector('.SidebarCart__shell'), { childList: true });
    sync();
};

$(initializeCart);
